
// Import necessary React hooks, Material-UI components, and Swiper modules
import React, { useState } from "react";
import "./HappyClient.css";
import { Box, Typography, IconButton, Modal, Backdrop } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import WestIcon from "@mui/icons-material/West";
import EastIcon from "@mui/icons-material/East";
import CloseIcon from "@mui/icons-material/Close";
import { fitnessHeroImages } from "../../../config/config"; // Adjust the import path as necessary

// Define an array of fitness hero image objects with source and alt text
const images = [
  { src: fitnessHeroImages.fitnesssHero01, alt: "Fitness Hero 1" },
  { src: fitnessHeroImages.fitnesssHero02, alt: "Fitness Hero 2" },
  { src: fitnessHeroImages.fitnesssHero03, alt: "Fitness Hero 3" },
  { src: fitnessHeroImages.fitnesssHero04, alt: "Fitness Hero 4" },
  { src: fitnessHeroImages.fitnesssHero05, alt: "Fitness Hero 5" },
  { src: fitnessHeroImages.fitnesssHero06, alt: "Fitness Hero 6" },
  { src: fitnessHeroImages.fitnesssHero07, alt: "Fitness Hero 7" },
  { src: fitnessHeroImages.fitnesssHero08, alt: "Fitness Hero 8" },
  { src: fitnessHeroImages.fitnesssHero09, alt: "Fitness Hero 9" },
  { src: fitnessHeroImages.fitnesssHero10, alt: "Fitness Hero 10" },
  { src: fitnessHeroImages.fitnesssHero11, alt: "Fitness Hero 11" },
  { src: fitnessHeroImages.fitnesssHero12, alt: "Fitness Hero 12" },
];

// HappyClient component to display a quote and image slider
const HappyClient = () => {
  // State to manage the selected image, gallery visibility, current image index, and swiper instance
  const [selectedImage, setSelectedImage] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState(null);

  // Function to open the gallery modal with the selected image
  const openGallery = (index) => {
    setCurrentImageIndex(index);
    setSelectedImage(images[index]);
    setGalleryOpen(true);
  };

  // Function to close the gallery modal
  const closeGallery = () => {
    setGalleryOpen(false);
    setSelectedImage(null);
  };

  // Function to navigate to the next image in the gallery
  const nextImage = () => {
    const nextIndex = (currentImageIndex + 1) % images.length;
    setCurrentImageIndex(nextIndex);
    setSelectedImage(images[nextIndex]);
  };

  // Function to navigate to the previous image in the gallery
  const prevImage = () => {
    const prevIndex =
      currentImageIndex === 0 ? images.length - 1 : currentImageIndex - 1;
    setCurrentImageIndex(prevIndex);
    setSelectedImage(images[prevIndex]);
  };

  // Effect to handle keyboard navigation for the gallery modal
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (!galleryOpen) return;
      
      if (event.key === 'ArrowLeft') {
        prevImage();
      } else if (event.key === 'ArrowRight') {
        nextImage();
      } else if (event.key === 'Escape') {
        closeGallery();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [galleryOpen, currentImageIndex]);

  // Function to navigate to the previous slide in the Swiper
  const handlePrevSlide = () => {
    if (swiperInstance) {
      swiperInstance.slidePrev();
    }
  };

  // Function to navigate to the next slide in the Swiper
  const handleNextSlide = () => {
    if (swiperInstance) {
      swiperInstance.slideNext();
    }
  };

  return (
    // Main container for the HappyClient section
    <div>
      <Box sx={{ backgroundColor: "#111111", py: { xs: 4, sm: 6, md: 8 } }}>
        <div className="header-content">
          <Box
            className="container"
            sx={{
              px: { xs: 2, sm: 3, md: 4, lg: 0 },
              maxWidth: "1200px",
              margin: "0 auto",
              width: "100%",
            }}
          >
            <div className="quote-container">
              {/* Desktop layout for quote and navigation */}
              <div className="desktop-layout quote-main">
                <div className="quote-text">
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: "#fff",
                      fontSize: { 
                        xs: "18px", 
                        sm: "22px", 
                        md: "28px", 
                        lg: "32px",
                        xl: "36px"
                      },
                      lineHeight: { xs: 1.3, sm: 1.2 },
                      textAlign: "start",
                    }}
                  >
                    "You don't need motivation. You need a structured system that's
                    easy to follow — and impossible to outgrow."
                  </Typography>
                </div>

                {/* Navigation buttons for desktop */}
                <div className="navigation-buttons">
                  <IconButton 
                    className="prev-btn-desktop" 
                    onClick={handlePrevSlide}
                  >
                    <WestIcon />
                  </IconButton>
                  <IconButton 
                    className="next-btn-desktop" 
                    onClick={handleNextSlide}
                  >
                    <EastIcon />
                  </IconButton>
                </div>
              </div>

              {/* Quote attribution for desktop */}
              <div className="quote-attribution desktop-layout">
                <Typography 
                  sx={{
                    color: '#fff',
                    fontSize: { xs: '12px', sm: '14px', md: '16px' },
                    fontWeight: 400,
                    opacity: 0.8,
                    textAlign: 'right',
                  }}
                >
                  — Paul Gradwell, Body Recomposition Specialist
                </Typography>
              </div>

              {/* Responsive layout for tablet and mobile */}
              <div className="responsive-layout">
                <div className="quote-text">
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: "#fff",
                      fontSize: { 
                        xs: "18px", 
                        sm: "22px", 
                        md: "28px"
                      },
                      lineHeight: { xs: 1.3, sm: 1.2 },
                      textAlign: "start",
                    }}
                  >
                    "You don't need motivation. You need a structured system that's
                    easy to follow — and impossible to outgrow."
                  </Typography>
                </div>

                {/* Quote attribution for responsive layout */}
                <div className="quote-attribution">
                  <Typography 
                    sx={{
                      color: '#fff',
                      fontSize: { xs: '12px', sm: '14px' },
                      fontWeight: 400,
                      opacity: 0.8,
                      textAlign: 'left',
                    }}
                  >
                    — Paul Gradwell, Body Recomposition Specialist
                  </Typography>
                </div>

                {/* Navigation buttons for responsive layout */}
                <div className="navigation-buttons">
                  <IconButton 
                    className="prev-btn-responsive" 
                    onClick={handlePrevSlide}
                  >
                    <WestIcon />
                  </IconButton>
                  <IconButton 
                    className="next-btn-responsive" 
                    onClick={handleNextSlide}
                  >
                    <EastIcon />
                  </IconButton>
                </div>
              </div>
            </div>
          </Box>
        </div>

        {/* Swiper component for image slider */}
        <Box sx={{ 
          margin: { xs: "40px 0 20px 0", sm: "50px 0 20px 0", md: "70px 0 20px 0" },
          px: { xs: 1, sm: 2 }
        }}>
          <Swiper
            modules={[Navigation]}
            spaceBetween={16}
            slidesPerView={1.2}
            breakpoints={{
              360: { slidesPerView: 1.3, spaceBetween: 12 },
              480: { slidesPerView: 1.8, spaceBetween: 14 },
              640: { slidesPerView: 2.2, spaceBetween: 16 },
              768: { slidesPerView: 2.5, spaceBetween: 16 },
              1024: { slidesPerView: 3.2, spaceBetween: 18 },
              1200: { slidesPerView: 3.8, spaceBetween: 20 },
              1400: { slidesPerView: 4.2, spaceBetween: 20 },
            }}
            onSwiper={setSwiperInstance}
          >
            {images.map((image, index) => (
              <SwiperSlide key={index}>
                <Box
                  onClick={() => openGallery(index)}
                  sx={{
                    borderRadius: "12px",
                    aspectRatio: "4 / 3",
                    border: "2px solid #222",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    overflow: "hidden",
                    position: "relative",
                    "&:hover": {
                      transform: "scale(1.05)",
                      border: "2px solid #00BCD4",
                      boxShadow: "0 4px 20px rgba(0, 188, 212, 0.3)",
                    },
                  }}
                  title={image.alt}
                >
                  {/* Blurred background image for visual effect */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: `url(${image.src})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      filter: "blur(8px) brightness(0.3)",
                      zIndex: 1,
                    }}
                  />

                  {/* Main image displayed over the blurred background */}
                  <Box
                    sx={{
                      position: "relative",
                      zIndex: 2,
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        transition: "all 0.3s ease",
                        position: "relative",
                        zIndex: 2,
                      }}
                    />
                  </Box>
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      </Box>

      {/* Modal for full-screen image gallery */}
      <Modal
        open={galleryOpen}
        onClose={closeGallery}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: "rgba(0, 0, 0, 0.9)" },
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Box
          sx={{
            position: "relative",
            maxWidth: "90vw",
            maxHeight: "90vh",
            outline: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {selectedImage && (
            <>
              {/* Button to close the modal */}
              <IconButton
                onClick={closeGallery}
                sx={{
                  position: "absolute",
                  top: { xs: -30, sm: -40 },
                  right: { xs: -30, sm: -40 },
                  color: "#fff",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  zIndex: 1000,
                  width: { xs: 35, sm: 40 },
                  height: { xs: 35, sm: 40 },
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
              </IconButton>

              {/* Button to navigate to the previous image */}
              <IconButton
                onClick={prevImage}
                sx={{
                  position: "absolute",
                  left: { xs: -40, sm: -60 },
                  color: "#fff",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  zIndex: 1000,
                  width: { xs: 35, sm: 40 },
                  height: { xs: 35, sm: 40 },
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                  },
                }}
              >
                <WestIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
              </IconButton>

              {/* Main image in the modal */}
              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
              />

              {/* Button to navigate to the next image */}
              <IconButton
                onClick={nextImage}
                sx={{
                  position: "absolute",
                  right: { xs: -40, sm: -60 },
                  color: "#fff",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  zIndex: 1000,
                  width: { xs: 35, sm: 40 },
                  height: { xs: 35, sm: 40 },
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                  },
                }}
              >
                <EastIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
              </IconButton>

              {/* Image counter display */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: { xs: -30, sm: -40 },
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: "#fff",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.5, sm: 1 },
                  borderRadius: "20px",
                  fontSize: { xs: "12px", sm: "14px" },
                  whiteSpace: "nowrap",
                }}
              >
                {currentImageIndex + 1} / {images.length}
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
};

// Export the HappyClient component
export default HappyClient;